import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
interface PrivacyPageProps {
  onBack: () => void;
}
export function PrivacyPage({
  onBack
}: PrivacyPageProps) {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  return <div className="min-h-screen w-full bg-dark-bg font-inter antialiased" data-id="element-340">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-id="element-341">
        {/* Back button */}
        <motion.button onClick={onBack} className="inline-flex items-center gap-2 text-concrete/60 hover:text-orange-accent transition-colors duration-200 mb-12 font-inter text-sm" initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-342">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-343" />
          Back to main page
        </motion.button>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-344">
          <h1 className="font-bebas text-4xl md:text-5xl text-white tracking-wide mb-2" data-id="element-345">
            PRIVACY <span className="text-orange-accent" data-id="element-346">POLICY</span>
          </h1>
          <p className="font-inter text-concrete/60 text-sm mb-12" data-id="element-347">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>

          <div className="space-y-10 font-inter text-concrete leading-relaxed" data-id="element-348">
            <section data-id="element-349">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-350">
                1. OUR COMMITMENT TO YOUR PRIVACY
              </h2>
              <p data-id="element-351">
                We understand that privacy isn't just a preference for our
                customers — it's a core value. Concrete Bunker Blueprint is
                designed from the ground up to minimize data collection. We
                collect only what is strictly necessary to process your purchase
                and deliver your product.
              </p>
            </section>

            <section data-id="element-352">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-353">
                2. INFORMATION WE COLLECT
              </h2>
              <p className="mb-3" data-id="element-354">
                We collect only the minimum information required to complete
                your transaction:
              </p>
              <ul className="list-none space-y-2 pl-4 mb-3" data-id="element-355">
                <li className="flex gap-2" data-id="element-356">
                  <span className="text-orange-accent" data-id="element-357">—</span>{' '}
                  <strong className="text-white" data-id="element-358">Email address</strong> — to
                  deliver your download link and receipt
                </li>
                <li className="flex gap-2" data-id="element-359">
                  <span className="text-orange-accent" data-id="element-360">—</span>{' '}
                  <strong className="text-white" data-id="element-361">Payment information</strong> —
                  processed securely by our payment processor (we never store
                  card data)
                </li>
                <li className="flex gap-2" data-id="element-362">
                  <span className="text-orange-accent" data-id="element-363">—</span>{' '}
                  <strong className="text-white" data-id="element-364">IP address</strong> — collected
                  automatically by our hosting provider for security purposes
                </li>
              </ul>
              <p data-id="element-365">
                We do <strong className="text-white" data-id="element-366">not</strong> collect your
                name, phone number, physical address, or any identifying
                information beyond what is listed above.
              </p>
            </section>

            <section data-id="element-367">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-368">
                3. WHAT WE DO NOT DO
              </h2>
              <p className="mb-3" data-id="element-369">We will never:</p>
              <ul className="list-none space-y-2 pl-4" data-id="element-370">
                <li className="flex gap-2" data-id="element-371">
                  <span className="text-orange-accent" data-id="element-372">—</span> Sell, rent, or
                  trade your email address to third parties
                </li>
                <li className="flex gap-2" data-id="element-373">
                  <span className="text-orange-accent" data-id="element-374">—</span> Add you to
                  marketing lists without your explicit consent
                </li>
                <li className="flex gap-2" data-id="element-375">
                  <span className="text-orange-accent" data-id="element-376">—</span> Share your
                  purchase history with any government agency absent a valid
                  legal order
                </li>
                <li className="flex gap-2" data-id="element-377">
                  <span className="text-orange-accent" data-id="element-378">—</span> Use tracking
                  pixels or behavioral advertising tools on this page
                </li>
                <li className="flex gap-2" data-id="element-379">
                  <span className="text-orange-accent" data-id="element-380">—</span> Store your
                  payment details on our servers
                </li>
              </ul>
            </section>

            <section data-id="element-381">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-382">
                4. HOW WE USE YOUR INFORMATION
              </h2>
              <p className="mb-3" data-id="element-383">Your email address is used exclusively to:</p>
              <ul className="list-none space-y-2 pl-4" data-id="element-384">
                <li className="flex gap-2" data-id="element-385">
                  <span className="text-orange-accent" data-id="element-386">—</span> Send your
                  purchase confirmation and download link
                </li>
                <li className="flex gap-2" data-id="element-387">
                  <span className="text-orange-accent" data-id="element-388">—</span> Process refund
                  requests if applicable
                </li>
                <li className="flex gap-2" data-id="element-389">
                  <span className="text-orange-accent" data-id="element-390">—</span> Respond to
                  customer support inquiries you initiate
                </li>
              </ul>
            </section>

            <section data-id="element-391">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-392">
                5. PAYMENT PROCESSING
              </h2>
              <p data-id="element-393">
                All payment transactions are processed by a PCI-compliant
                third-party payment processor. We do not have access to your
                full credit card number at any time. Your financial data is
                governed by the payment processor's own privacy policy and
                security standards.
              </p>
            </section>

            <section data-id="element-394">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-395">
                6. COOKIES & TRACKING
              </h2>
              <p data-id="element-396">
                This website uses minimal session cookies required for basic
                functionality. We do not use advertising cookies, cross-site
                tracking, or third-party analytics that identify individual
                users. Any analytics we use are aggregated and anonymized.
              </p>
            </section>

            <section data-id="element-397">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-398">
                7. DATA RETENTION
              </h2>
              <p data-id="element-399">
                Purchase records are retained for the minimum period required by
                applicable tax and financial regulations (typically 7 years).
                After this period, records are permanently deleted. You may
                request deletion of your data at any time by contacting us,
                subject to legal retention requirements.
              </p>
            </section>

            <section data-id="element-400">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-401">
                8. YOUR RIGHTS
              </h2>
              <p className="mb-3" data-id="element-402">You have the right to:</p>
              <ul className="list-none space-y-2 pl-4" data-id="element-403">
                <li className="flex gap-2" data-id="element-404">
                  <span className="text-orange-accent" data-id="element-405">—</span> Request a copy
                  of any personal data we hold about you
                </li>
                <li className="flex gap-2" data-id="element-406">
                  <span className="text-orange-accent" data-id="element-407">—</span> Request
                  correction of inaccurate data
                </li>
                <li className="flex gap-2" data-id="element-408">
                  <span className="text-orange-accent" data-id="element-409">—</span> Request deletion
                  of your data (subject to legal requirements)
                </li>
                <li className="flex gap-2" data-id="element-410">
                  <span className="text-orange-accent" data-id="element-411">—</span> Opt out of any
                  future communications at any time
                </li>
              </ul>
            </section>

            <section data-id="element-412">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-413">
                9. LEGAL DISCLOSURE
              </h2>
              <p data-id="element-414">
                We will only disclose your information to law enforcement or
                government agencies if required by a valid, lawful court order
                or subpoena. We will notify you of any such request to the
                extent permitted by law.
              </p>
            </section>

            <section data-id="element-415">
              <h2 className="font-bebas text-2xl text-white tracking-wide mb-3" data-id="element-416">
                10. CONTACT
              </h2>
              <p data-id="element-417">
                For privacy-related requests or questions, contact us at:{' '}
                <span className="text-orange-accent" data-id="element-418">
                  privacy@concretebunkerblueprint.com
                </span>
              </p>
            </section>
          </div>
        </motion.div>

        {/* Back button bottom */}
        <motion.button onClick={onBack} className="inline-flex items-center gap-2 text-concrete/60 hover:text-orange-accent transition-colors duration-200 mt-16 font-inter text-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-419">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-420" />
          Back to main page
        </motion.button>
      </div>
    </div>;
}